import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../components/button";
import { H1 } from "../components/h1";
import { Input } from "../components/input";
import { login } from "../services/auth";
import { useAuthStore } from "../stores/auth";
import { useLocale } from "../stores/locale";

export function Login({
  registerPath,
  resetPasswordPath,
  afterLoginPath = "/",
}: {
  registerPath?: string;
  resetPasswordPath?: string;
  afterLoginPath?: string;
} = {}) {
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const { locale, currentLanguage } = useLocale();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    setIsValid(email.length > 0 && password.length > 0);
  }, [email, password]);

  return (
    <form
      className="mx-auto flex w-full max-w-xl flex-col gap-3"
      onSubmit={(e) => {
        (async () => {
          setIsLoading(true);
          e.preventDefault();
          try {
            await login(email, password, currentLanguage);
            authStore.setIsAuth(true);
            navigate(afterLoginPath);
          } catch (error) {
            setHasError(true);
          } finally {
            setIsLoading(false);
          }
        })();
      }}
    >
      <H1>{locale.login.title}</H1>
      <Input
        disabled={isLoading}
        hasError={hasError}
        placeholder={locale.login.email}
        type="email"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setEmail(e.target.value)
        }
      />
      <Input
        disabled={isLoading}
        hasError={hasError}
        placeholder={locale.login.password}
        type="password"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setPassword(e.target.value)
        }
      />
      <div className="mt-2 flex items-center justify-end gap-2">
        {hasError && <p className="text-red-500">{locale.login.error}</p>}
        <Button disabled={!isValid || isLoading} type="submit">
          {locale.login.submit}
        </Button>
      </div>
      {(registerPath || resetPasswordPath) && (
        <div className="flex flex-col gap-2 text-sm">
          {registerPath && (
            <Link className="text-blue-700 hover:underline" to={registerPath}>
              {locale.auth.registerLink}
            </Link>
          )}
          {resetPasswordPath && (
            <Link
              className="text-blue-700 hover:underline"
              to={resetPasswordPath}
            >
              {locale.auth.resetPasswordLink}
            </Link>
          )}
        </div>
      )}
    </form>
  );
}
